import { ApolloClient } from '@apollo/client';
import { createAction } from 'redux-actions';

import { CALL_API, LocalStorageKeys } from 'src/common/constants';
import { Products } from 'src/common/enums';
import { stringifyPhoneNumber } from 'src/common/phoneNumberInputHelper';
import Schemas from 'src/common/schemas';
import { SignUpOrLoginType } from 'src/common/tracking/constants';
import { trackUserSignUpOrLogin } from 'src/common/tracking/trackingUtils';
import { hasLocalStorage } from 'src/common/utils/hasLocalStorage';
import { SignInSource } from 'src/components/LoginModal/ExternalSignInButtons';
import { UserRole } from 'src/global/models/User';
import { ReduxThunkAction } from 'src/global/store';
import { getConfig } from 'src/modules/ClientConfig';
import {
  getSessionToken,
  getSignUpSuccess,
} from 'src/modules/Session/Selectors';
import { setIsNewSignUp } from 'src/modules/Virality/Actions';
import { getLocale } from 'src/selectors/locale';

import { trackClickEvent } from '../GlintsTracking/tracking';
import {
  Actions,
  cleanDataWhenLogout,
  clearToken,
  clearUserMeta,
  fetchUserData,
  logout,
  setRoleMismatch,
  setTokenIsNotValid,
  updateUserMeta,
} from './ActionsCommon';
import * as GTM from './GTMActions';

export { Actions };
export { setTokenIsNotValid };
export { setRoleMismatch };

/**
 * Sets sentAt in redux.
 * If the user's browser has local storage, also stores sentAt in local storage.
 */
export const setEmailVerificationSentAt = (sentAt: number) => {
  if (hasLocalStorage()) {
    localStorage.setItem(
      LocalStorageKeys.emailVerificationRequestSentAt,
      sentAt.toString()
    );
  }
  return {
    type: Actions.SET_EMAIL_VERIFICATION_SENT_AT,
    payload: sentAt,
  };
};

/**
 * Resets sentAt in redux.
 * If the user's browser has local storage, also removes sentAt from local storage.
 */
export const resetEmailVerificationSentAt = () => {
  if (hasLocalStorage()) {
    localStorage.removeItem(LocalStorageKeys.emailVerificationRequestSentAt);
  }
  return {
    type: Actions.RESET_EMAIL_VERIFICATION_SENT_AT,
  };
};

export const requestToken = createAction(Actions.REQUEST_TOKEN);

export const receiveToken = createAction(
  Actions.RECEIVE_TOKEN,
  (token: string, err: Error) => (err ? err : token)
);
export { clearToken };
export const updateSocialMediaSignup = createAction(
  Actions.UPDATE_SOCIAL_MEDIA_SIGNUP
);
export const clearSocialMediaSignup = createAction(
  Actions.CLEAR_SOCIAL_MEDIA_SIGNUP
);
export const clearReceiveTokenError = createAction(
  Actions.CLEAR_RECEIVE_TOKEN_ERROR
);

export const clearCreateUserError = createAction(
  Actions.CLEAR_CREATE_USER_ERROR
);

export { updateUserMeta };
export { clearUserMeta };

export const sendEmailVerificationTokenRequest = createAction(
  Actions.SEND_EMAIL_VERIFICATION_TOKEN_REQUEST
);
export const sendEmailVerificationTokenSuccess = createAction(
  Actions.SEND_EMAIL_VERIFICATION_TOKEN_SUCCESS
);
export const sendEmailVerificationTokenFailure = createAction(
  Actions.SEND_EMAIL_VERIFICATION_TOKEN_FAILURE
);

export const resetSendEmailVerificationToken = createAction(
  Actions.RESET_SEND_EMAIL_VERIFICATION_TOKEN
);

export interface LoginGTMOptions {
  productSource?: Products;
  shouldDispatchLoginSuccess: boolean;
}

export { fetchUserData };

/* We only want to trigger the GTM action upon successful login after
login button click. Since the signup action also uses this login action,
we add the boolean flag to determine when to trigger the GTM action  */
export function login(
  email: string,
  password: string,
  apolloClient?: ApolloClient<object>,
  gtmOptions = {
    shouldDispatchLoginSuccess: true,
    productSource: Products.Marketplace,
  } as LoginGTMOptions
): ReduxThunkAction<any> {
  return async (dispatch, getState, { api }) => {
    const config = getConfig(getState());
    const { shouldDispatchLoginSuccess = true } = gtmOptions;

    try {
      dispatch(requestToken());
      const response = await api(null, config.OAUTH2_BASE).post('token', {
        grant_type: 'password',
        client_id: config.CLIENT_ID,
        username: email,
        password,
        role: UserRole.CANDIDATE,
      });

      dispatch(receiveToken(response.data['access_token'], null));
    } catch (err) {
      dispatch(receiveToken(null, err));
      dispatch(GTM.LoginWithEmailFailure());
    }

    const sessionToken = getSessionToken(getState());

    if (sessionToken) {
      await dispatch(fetchUserData(apolloClient));

      if (shouldDispatchLoginSuccess) {
        trackUserSignUpOrLogin({
          type: SignUpOrLoginType.Email,
          isSignUp: false,
          success: true,
        });
      }
    }
  };
}

export { cleanDataWhenLogout };
export { logout };

function trackSignUpSuccess(data: { email: string; phone: string }) {
  /* eslint-disable camelcase */
  trackClickEvent({
    elementName: 'signup_button',
    properties: {
      success: true,
      error_message: '',
      email: data.email,
      phone_number: data.phone,
    },
  });
  /* eslint-enable camelcase */
}

function trackSignUpFailure(data: {
  email: string;
  phone: string;
  errorMessage: string;
}) {
  /* eslint-disable camelcase */
  trackClickEvent({
    elementName: 'signup_button',
    properties: {
      success: false,
      error_message: data.errorMessage,
      email: data.email,
      phone_number: data.phone,
    },
  });
  /* eslint-enable camelcase */
}

export function signUp(
  email: string,
  password: string,
  firstName: string,
  lastName: string,
  phone: string,
  locationId: string,
  CountryCode: string,
  sendUpdates: boolean,
  gtmPayloadString = Products.Marketplace,
  whatsappNumber?: string,
  apolloClient?: ApolloClient<object>
): ReduxThunkAction<any> {
  return async (dispatch, getState) => {
    const data = {
      email,
      password,
      firstName,
      lastName,
      CountryCode,
      metadata: { sendUpdates },
      LocationId: locationId,
      preferredLanguage: getLocale(getState()).language,
      ...(whatsappNumber && { whatsappNumber: whatsappNumber }),
      ...(phone && { phone: stringifyPhoneNumber(JSON.parse(phone)) }),
    };

    await dispatch({
      [CALL_API]: {
        types: [
          Actions.CREATE_USER_REQUEST,
          Actions.CREATE_USER_SUCCESS,
          Actions.CREATE_USER_FAILURE,
        ],
        endpoint: 'users',
        schema: Schemas.USER,
        method: 'post',
        options: {
          data: data,
        },
        hooks: {
          successPayload: gtmPayloadString,
          onError: (error: Error) => {
            trackSignUpFailure({
              email: data.email,
              phone: data.whatsappNumber ?? data.phone ?? '',
              errorMessage: error.message || 'Unknown error',
            });
          },
        },
      },
    });

    const signupSuc = getSignUpSuccess(getState());

    if (signupSuc) {
      dispatch(setIsNewSignUp(true));
      await dispatch(
        login(email, password, apolloClient, {
          shouldDispatchLoginSuccess: false,
        })
      );

      trackUserSignUpOrLogin({
        type: SignUpOrLoginType.Email,
        isSignUp: true,
        success: true,
      });
      trackSignUpSuccess({
        email: data.email,
        phone: data.whatsappNumber ?? data.phone ?? '',
      });
    }
  };
}

export function loginWithGoogle(
  token: string,
  gtmData: any,
  signInSource: SignInSource,
  apolloClient?: ApolloClient<object>
): ReduxThunkAction<any> {
  return async (dispatch, getState, { api }) => {
    dispatch(requestToken());
    const config = getConfig(getState());
    let isSignUp = false;
    let trackingSource: 'Google one tap' | undefined;
    try {
      const response = await api(null, config.OAUTH2_BASE).post('/google', {
        idToken: token,
        apiClientId: config.CLIENT_ID,
        redirectUri: `${
          document.getElementsByTagName('base')[0].href
        }oauth2/google`,
      });
      dispatch(receiveToken(response.data.token, null));

      trackingSource =
        signInSource === 'one tap' ? 'Google one tap' : undefined;
      isSignUp = response.data.signup;
      if (isSignUp) {
        dispatch(updateSocialMediaSignup('google'));
        dispatch(setIsNewSignUp(true));
      }
    } catch (err) {
      dispatch(receiveToken(null, err));
      dispatch(
        GTM.LoginWithGoogleFailed({
          source: signInSource,
          errorMessage: err?.message,
        })
      );
    }

    if (getSessionToken(getState())) {
      await dispatch(fetchUserData(apolloClient));

      trackUserSignUpOrLogin({
        type: SignUpOrLoginType.Google,
        isSignUp,
        source: trackingSource,
        success: true,
      });
    }
  };
}

export function sendEmailVerificationToken(
  onSuccess?: () => void
): ReduxThunkAction<any> {
  return async (dispatch, getState, { api }) => {
    try {
      dispatch(sendEmailVerificationTokenRequest());
      await api().post('/users/sendEmailVerificationToken');
      dispatch(sendEmailVerificationTokenSuccess());
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      dispatch(sendEmailVerificationTokenFailure(err));
    }
  };
}

interface VerifyEmailParams {
  email: string;
  id: string;
  token: string;
}

export function verifyEmail({
  token,
  id,
  email,
}: VerifyEmailParams): ReduxThunkAction<any> {
  return async (dispatch) => {
    await dispatch({
      [CALL_API]: {
        types: [
          Actions.VERIFY_EMAIL_REQUEST,
          Actions.VERIFY_EMAIL_SUCCESS,
          Actions.VERIFY_EMAIL_FAILURE,
        ],
        endpoint: 'users/verifyEmail',
        schema: Schemas.USER,
        method: 'post',
        options: {
          token,
          id,
          email,
        },
      },
    });
  };
}
